import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../../shared/api/client";
import type { AuditEntry } from "../../shared/api/types";
import { formatDateTime, useI18n } from "../../shared/i18n";
import { Icon } from "../../shared/ui/icons";
import { Select } from "../../shared/ui/Select";
import { AdminTabs } from "./AdminTabs";
import { SkeletonRows } from "../../shared/ui/Skeleton";

const PAGE = 100;

interface Options {
  modules: { module: string; n: number }[];
  actions: { action: string; n: number }[];
  actors: { actor_id: string | null; actor_email: string; name: string; n: number }[];
  span: { earliest: string | null; latest: string | null };
}

interface Filters {
  q: string;
  module: string;
  action: string;
  actor_id: string;
  date_from: string;
  date_to: string;
  hide_logins: boolean;
}

const EMPTY: Filters = {
  q: "",
  module: "",
  action: "",
  actor_id: "",
  date_from: "",
  date_to: "",
  hide_logins: false,
};

export function AuditPage() {
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [options, setOptions] = useState<Options | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  /** The search box is debounced; every other control applies at once. Without the
   *  debounce, typing "reset" fires five queries against a growing table. */
  const [typed, setTyped] = useState("");
  // First load only, so a refetch keeps the current rows rather than replacing a
  // populated table with placeholders on every change.
  const [firstLoad, setFirstLoad] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(
      () => setFilters((f) => (f.q === typed ? f : { ...f, q: typed })),
      300,
    );
    return () => window.clearTimeout(id);
  }, [typed]);

  const query = useCallback((extra: Record<string, string | number> = {}) => {
    const p = new URLSearchParams({ limit: String(PAGE) });
    if (filters.q) p.set("q", filters.q);
    if (filters.module) p.set("module", filters.module);
    if (filters.action) p.set("action", filters.action);
    if (filters.actor_id) p.set("actor_id", filters.actor_id);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    if (filters.hide_logins) p.set("hide_logins", "true");
    for (const [k, v] of Object.entries(extra)) p.set(k, String(v));
    return p.toString();
  }, [filters]);

  // A request counter, so a slow earlier response cannot overwrite a newer one. With
  // a debounced search box that reordering is easy to hit and looks like the filter
  // being ignored.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setBusy(true);
    try {
      const r = await api.get<{
        entries: AuditEntry[]; total: number; options: Options;
      }>(`/audit?${query()}`);
      if (mine !== seq.current) return;
      setEntries(r.entries);
      setTotal(r.total);
      setOptions(r.options);
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(err instanceof ApiError ? err.message : t("audit.loadFailed"));
    } finally {
      if (mine === seq.current) {
        setBusy(false);
        // Cleared here rather than on the success path: a failed first load must show
        // the error, not shimmer forever. Guarded by the sequence check for the same
        // reason as setBusy -- a superseded response must not touch the UI at all.
        setFirstLoad(false);
      }
    }
  }, [query, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Keyset paging, not offset: rows keep arriving at the top, and an offset would
   *  re-show whatever the first page already displayed. */
  async function loadOlder() {
    const oldest = entries[entries.length - 1];
    if (!oldest) return;
    setBusy(true);
    try {
      const r = await api.get<{ entries: AuditEntry[]; total: number }>(
        `/audit?${query({ before_id: oldest.id })}`,
      );
      setEntries((prev) => [...prev, ...r.entries]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("audit.loadFailed"));
    } finally {
      setBusy(false);
    }
  }

  const active = useMemo(
    () => Object.keys(EMPTY).filter((k) => filters[k as keyof Filters] !== EMPTY[k as keyof Filters]),
    [filters],
  );

  function clear() {
    setTyped("");
    setFilters(EMPTY);
  }

  const moreToLoad = entries.length < total;

  return (
    <div className="stack">
      <AdminTabs />
      {error && (
        <p className="banner error" role="alert">
          {error}
        </p>
      )}

      <h2 className="section-title">{t("audit.title")}</h2>
      <p className="muted small">{t("audit.help")}</p>

      <section className="filters card" aria-label={t("audit.filters")}>
        <div className="filter-grid">
          <label className="filter wide">
            <span>{t("audit.search")}</span>
            <input
              type="search"
              value={typed}
              placeholder={t("audit.searchPlaceholder")}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>

          <label className="filter">
            <span>{t("audit.action")}</span>
            <Select
              value={filters.action}
              onChange={(v) => setFilters((f) => ({ ...f, action: v }))}
              ariaLabel={t("audit.action")}
              options={[
                { value: "", label: t("audit.all") },
                ...(options?.actions ?? []).map((a) => ({
                  value: a.action,
                  label: a.action,
                  // A count belongs beside the label, not glued into it: typeahead
                  // matches on the label, and "login (1329)" is not what anyone types.
                  meta: String(a.n),
                })),
              ]}
            />
          </label>

          <label className="filter">
            <span>{t("audit.actor")}</span>
            <Select
              value={filters.actor_id}
              onChange={(v) => setFilters((f) => ({ ...f, actor_id: v }))}
              ariaLabel={t("audit.actor")}
              options={[
                { value: "", label: t("audit.all") },
                ...(options?.actors ?? [])
                  .filter((a) => a.actor_id)
                  .map((a) => ({
                    value: a.actor_id!,
                    label: a.name,
                    description: a.actor_email,
                    meta: String(a.n),
                  })),
              ]}
            />
          </label>

          {/* Only shown once there is more than one module to choose between. */}
          {(options?.modules.length ?? 0) > 1 && (
            <label className="filter">
              <span>{t("audit.module")}</span>
              <Select
                value={filters.module}
                onChange={(v) => setFilters((f) => ({ ...f, module: v }))}
                ariaLabel={t("audit.module")}
                options={[
                  { value: "", label: t("audit.all") },
                  ...(options?.modules ?? []).map((m) => ({
                    value: m.module,
                    label: m.module,
                    meta: String(m.n),
                  })),
                ]}
              />
            </label>
          )}

          <label className="filter">
            <span>{t("audit.from")}</span>
            <input
              type="date"
              value={filters.date_from}
              max={filters.date_to || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </label>

          <label className="filter">
            <span>{t("audit.to")}</span>
            <input
              type="date"
              value={filters.date_to}
              min={filters.date_from || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </label>
        </div>

        <div className="filter-actions">
          {/* Sign-ins are the overwhelming majority of rows, so suppressing them is a
              first-class control rather than something to build with the action list. */}
          <label className="check">
            <input
              type="checkbox"
              checked={filters.hide_logins}
              onChange={(e) => setFilters((f) => ({ ...f, hide_logins: e.target.checked }))}
            />
            <span>{t("audit.hideLogins")}</span>
          </label>

          <span className="filter-count num">
            {busy
              ? t("audit.loading")
              : entries.length < total
                ? t("audit.showing", { n: entries.length, total })
                : t("audit.showingAll", { total })}
          </span>

          {active.length > 0 && (
            <button type="button" className="btn ghost small" onClick={clear}>
              {t("audit.clear")} ({active.length})
            </button>
          )}
        </div>
      </section>

      {entries.length === 0 && !busy ? (
        <div className="empty">
          <p>{t("audit.noResults")}</p>
        </div>
      ) : (
        <>
      {firstLoad ? (
        <SkeletonRows rows={6} columns={[2, 2, 2, 2, 1, 1]} label={t("audit.title")} />
      ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("audit.when")}</th>
                  <th>{t("audit.who")}</th>
                  <th>{t("audit.action")}</th>
                  <th>{t("audit.entity")}</th>
                  <th>{t("audit.ip")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <Fragment key={e.id}>
                    <tr>
                      <td className="num">{formatDateTime(e.at, lang)}</td>
                      <td>{e.actor_name ?? e.actor_email ?? t("audit.system")}</td>
                      <td>
                        <code>{e.action}</code>
                      </td>
                      <td>
                        {e.entity && (
                          <span>
                            {e.entity}{" "}
                            <code className="faint">{e.entity_id?.slice(0, 8)}</code>
                          </span>
                        )}
                      </td>
                      <td className="num faint">{e.ip ?? ""}</td>
                      <td>
                        {(e.before != null || e.after != null) && (
                          <button
                            type="button"
                            className="btn ghost small"
                            aria-expanded={expanded === e.id}
                            onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          >
                            {expanded === e.id ? t("audit.hide") : t("audit.diff")}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === e.id && (
                      <tr className="detail-row">
                        <td colSpan={6}>
                          <div className="grid-2">
                            <div>
                              <h4 className="section-title">{t("audit.before")}</h4>
                              <pre>{JSON.stringify(e.before, null, 2) ?? "—"}</pre>
                            </div>
                            <div>
                              <h4 className="section-title">{t("audit.after")}</h4>
                              <pre>{JSON.stringify(e.after, null, 2) ?? "—"}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
      )}

          {moreToLoad && (
            <div className="row-center">
              <button type="button" className="btn" onClick={() => void loadOlder()} disabled={busy}>
                <Icon name="chevron" size={15} />
                <span>{busy ? t("audit.loading") : t("audit.loadOlder")}</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
